import "./dynamic-tabs.scss";
import { getPicture } from '../../../assets/js/picture-maker';

const DEVICE = {
  desktop : "desktop",
  mobile  : "mobile",
} 
const META = {
  timeout   : 305,
  dataTab   : "data-tab",
  dataPanel : "data-panel",
}
const CLASS = {
  active          : "active",
  panelsContainer : "dynamic-tabs__panels",
  panel           : "dynamic-tabs__panels-panel", 
};

const getDynamicTabs = (data, customClass) => `
<section 
  class="dynamic-tabs ${customClass}"
>
  <nav class="nav nav-tabs" role="tablist">
    ${data.map(({itemId, isFirstTab, tabLabel}, index) => `
      <button
        id="${itemId}-tab"
        class="nav-link ${isFirstTab ? CLASS.active : ""}"
        ${META.dataTab}="${index}"
        role="tab"
        aria-controls="${itemId}"
        aria-selected="${isFirstTab ? "true" : "false"}"
        aria-expanded="${isFirstTab ? "true" : "false"}"
      >
        ${tabLabel}
        <i class="icon-fa-plus icon-2x"></i>
      </button>
    `).join("")}
  </nav>
  <section class="${CLASS.panelsContainer}">
    ${data.map(({content, itemId, pictureTemplate}, index) => `
      <div
        id="${itemId}"
        class="${CLASS.panel} ${index === 0 ? CLASS.active : ""}" 
        ${META.dataPanel}="${index}"
        role="tabpanel"
        aria-labelledby="${itemId}-tab"
      >
        <i class="icon-fa-cross icon-2x"></i>
        <article id="${itemId}-text">
          ${content}
        </article>
        ${pictureTemplate}
      </div>
    `).join("")}
  </section>
</section>
`;

/**
 * Custom Dynamic Tabs Web Component
 * 
 * Attributes:
 * 
 * - <component> - component-class: Establece un string como clases específicas para el componente.
 * 
 * - <component> - component-img-width: Establece el ancho de la imagen en el panel para dispositivos de escritorio.
 * 
 * - <component> - component-img-bg-color: Establece el color de fondo para la imagen en el panel.
 * 
 * - <li> - item-id: Establece el identificador único para cada pestaña y panel.
 * 
 * - <li> - tab-label: Establece el título que se mostrará en cada pestaña.
 * 
 * - <li> - image-desktop: Establece la imagen que se mostrará en dispositivos de escritorio en el panel correspondiente.
 * 
 * - <li> - image-tablet: Establece la imagen que se mostrará en dispositivos tablet en el panel correspondiente.
 * 
 * - <li> - image-mobile: Establece la imagen que se mostrará en dispositivos móviles en el panel correspondiente.
 * 
 * - <li> - image-alt: Establece el texto alternativo para las imágenes del panel.
 * 
 * ! New Features: (Not yet implemented)
 * 
 * Attributes:
 * 
 * - <component> - component-id: Establece el identificador único para el componente.
 * 
 * - <component> - component-bg-color: Establece el color de fondo para el componente.
 * 
 * - tab-bg-color: Establece el color de fondo para cada pestaña. 
 * 
 * - panel-bg-color: Establece el color de fondo para cada panel.
 */
class SuperTabs extends HTMLElement {
  constructor() {
    super();
    this.deviceType = this.getDeviceType();
    this.tabs = null;
    this.panelsContainer = null;
    this.panels = null;
    this.maxHeight = 0;
  }

  connectedCallback() {
    if (
      DEVELOPMENT || (
      window['__PRERENDER_INJECTED'] && 
      window['__PRERENDER_INJECTED'].render_text)
    ) this.render();

    this.setElementsAfterRender();
    this.handleToggleTabs();
    this.handleClosePanel();
  }

  //* SETUP FUNCTIONS
  getDeviceType() {
    return getComputedStyle(document.body, "::before").content.replace(/"/g, "");
  }

  //* RENDER FUNCTIONS
  /**
   * Renders the component with the tabs and panels
   */
  render() {
    // Obtenemos los contenidos de las pestañas y paneles
    const content = Array.from(this.querySelectorAll(":scope > li"));
    const dynamicTabsData = this.getData(content);

    // Obtenemos los estilos personalizados del componente
    const customClass = this.getAttribute("component-class") || '';
    const pictureWidth = this.getAttribute("component-img-width");
    const pictureBackground = this.getAttribute("component-img-bg-color");

    // Inyectamos el contenido HTML y los estilos personalizados al componente
    this.innerHTML = getDynamicTabs(dynamicTabsData, customClass);

    // Establecemos los estilos personalizados faltantes
    pictureWidth && this.style.setProperty(
      '--dynamic-tabs-picture-width', 
      pictureWidth
    );
    pictureBackground && this.style.setProperty(
      '--dynamic-tabs-picture-bg-color'
      , pictureBackground
    );
  }

  getData(tabsContent) {
    return tabsContent.map((tabContent, index) => ({
      content: tabContent.innerHTML,
      isFirstTab: (index === 0),
      itemId: tabContent.getAttribute("item-id") || index,
      pictureTemplate: getPicture(tabContent),
      tabLabel: tabContent.getAttribute("tab-label") || '',
    }));
  }

  //* EVENT HANDLERS
  /**
   * Sets global variables of the elements that were created after the render of the web component
   */
  setElementsAfterRender() {
    this.tabs = Array.from(this.querySelectorAll("button.nav-link"));
    this.panelsContainer = this.getElementsByClassName(CLASS.panelsContainer)[0];
    this.panels = Array.from(this.getElementsByClassName(CLASS.panel));
  }

  toggleActiveClass(element, isActive) {
    element.classList.toggle(CLASS.active, isActive);
    if(element.tagName === 'BUTTON') {
      element.setAttribute("aria-selected", isActive.toString());
      element.setAttribute("aria-expanded", isActive.toString());
    }
  }

  toggleActiveClassWithDelay(element, isActive) {
    setTimeout(() => {
      this.toggleActiveClass(element, isActive);
    }, META.timeout);
  }

  updatePanelsHeight() {
    const currentHeight = this.panelsContainer.offsetHeight;

    if (currentHeight > this.maxHeight) {
      this.maxHeight = currentHeight;
      this.panelsContainer.style.height = `${this.maxHeight}px`;
      this.panels.forEach((panel) => {
        panel.style.height = `${this.maxHeight}px`;
      });
    }
  }

  /**
   * The function which handles the toggle of the tabs
   */
  handleToggleTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        const clickedTab = e.currentTarget;
        const dataTabSelected = clickedTab.getAttribute(META.dataTab);

        // Update 'active' class in the tabs
        this.tabs.forEach((tab) => {
          const isActive = (tab === clickedTab);
          this.toggleActiveClass(tab, isActive);
        });

        // If deviceType is mobile, set panelsContent to display block
        if (this.deviceType === DEVICE.mobile) {
          this.panelsContainer.style.display = "block";
          document.documentElement.style.overflow = 'hidden';
        }

        // If deviceType is desktop, update the height of the panel
        if(this.deviceType === DEVICE.desktop) {
          this.updatePanelsHeight();
        }

        // Update 'active' class in the panels
        this.panels.forEach((panel) => {
          const isActive = panel.getAttribute(META.dataPanel) === dataTabSelected;
          (isActive && this.deviceType === DEVICE.desktop) 
            ? this.toggleActiveClassWithDelay(panel, isActive)
            : this.toggleActiveClass(panel, isActive);
        });
      });
    });
  }

  /**
   * The function which handles the closing of the panels in mobile devices
   */
  handleClosePanel() {
    if(this.deviceType === 'desktop') return;

    // Add event listener to close panel
    this.panels.forEach((panel) => {
      panel.addEventListener('click', (ev) => {
        ev.preventDefault();
        const panelData = panel.getAttribute(META.dataPanel);
        const correspondingTab = this.tabs.find(tab => tab.getAttribute(META.dataTab) === panelData);
        
        if (!correspondingTab) return;

        this.toggleActiveClass(correspondingTab, false);
        this.toggleActiveClass(panel, false);
        setTimeout(() => {
          this.panelsContainer.style.display = "none";
          document.documentElement.style.overflow = 'auto';
        }, META.timeout);
      });
    });
  }
}

customElements.define("custom-dynamic-tabs", SuperTabs);